import type { ReactNode } from 'react';
import { useLang } from '../../lib/language';

/**
 * Shared layout for the picker screens (subsidiary/branch/drawer).
 * Single source of truth for the framing, error display, back button,
 * and logout button. Each picker just provides its list of cards.
 */
export function PickerLayout({
  title, subtitle, error, children,
  onBack, backLabel, onLogout, busy,
}: {
  title: string;
  subtitle: string;
  error: string | null;
  children: ReactNode;
  onBack?: () => void;
  backLabel?: string;
  onLogout: () => void;
  busy: boolean;
}): JSX.Element {
  const { t } = useLang();
  return (
    <div
      style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 'clamp(14px, 4vw, 24px)',
        background: 'var(--bg, #0B1220)',
        color: 'var(--text, #E8EEF7)',
      }}
    >
      <div
        style={{
          width: '100%',
          maxWidth: 480,
          padding: 'clamp(20px, 5vw, 32px)',
          background: 'var(--surface, #131C2E)',
          border: '1px solid var(--border, #2A3650)',
          borderRadius: 12,
          boxShadow: '0 8px 32px rgba(0,0,0,0.4)',
        }}
      >
        <div style={{ textAlign: 'center', marginBottom: 20 }}>
          <h1 style={{ margin: '0 0 6px', fontSize: 20, fontWeight: 700 }}>{title}</h1>
          <p style={{ margin: 0, fontSize: 13, color: 'var(--text-muted, #94A3B8)' }}>
            {subtitle}
          </p>
        </div>

        {error && <ErrorBanner text={error} />}

        {children}

        <div style={{ display: 'flex', gap: 8, marginTop: 16 }}>
          {onBack && (
            <button onClick={onBack} disabled={busy} style={ghostButton}>
              {backLabel}
            </button>
          )}
          <button onClick={onLogout} disabled={busy} style={ghostButton}>
            {t({ en: 'Sign out', ar: 'تسجيل الخروج' })}
          </button>
        </div>
      </div>
    </div>
  );
}

export function PickerCard({
  title, subtitle, onClick, disabled,
}: {
  title: string;
  subtitle: string | null;
  onClick: () => void;
  disabled: boolean;
}): JSX.Element {
  return (
    <li style={{ marginBottom: 10 }}>
      <button
        onClick={onClick}
        disabled={disabled}
        style={{
          width: '100%',
          // 60px keeps the row a comfortable thumb target on a tablet, where
          // this is the first thing a cashier touches each shift.
          minHeight: 60,
          padding: '14px 16px',
          textAlign: 'start',
          background: 'var(--bg, #0B1220)',
          border: '1px solid var(--border, #2A3650)',
          borderRadius: 8,
          color: 'var(--text, #E8EEF7)',
          fontSize: 16,
          cursor: disabled ? 'not-allowed' : 'pointer',
          opacity: disabled ? 0.5 : 1,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          gap: 12,
        }}
        onMouseEnter={(e) => {
          if (!disabled) {
            (e.currentTarget as HTMLButtonElement).style.borderColor = 'var(--primary, #5478D4)';
          }
        }}
        onMouseLeave={(e) => {
          (e.currentTarget as HTMLButtonElement).style.borderColor = 'var(--border, #2A3650)';
        }}
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
          <span style={{ fontWeight: 600 }}>{title}</span>
          {subtitle && (
            <span style={{ fontSize: 12, color: 'var(--text-muted, #94A3B8)' }}>{subtitle}</span>
          )}
        </div>
        <span style={{ color: 'var(--text-muted, #94A3B8)', fontSize: 18 }}>›</span>
      </button>
    </li>
  );
}

export function EmptyState({
  title, message, userLabel, primaryLabel, onPrimary, secondaryLabel, onSecondary, busy,
}: {
  title: string;
  message: string;
  userLabel: string;
  primaryLabel: string;
  onPrimary: () => void;
  secondaryLabel: string;
  onSecondary: () => void;
  busy: boolean;
}): JSX.Element {
  return (
    <div style={{
      minHeight: '100vh',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: 24,
      background: 'var(--bg, #0B1220)',
      color: 'var(--text, #E8EEF7)',
    }}>
      <div style={{
        width: '100%',
        maxWidth: 480,
        padding: 32,
        background: 'var(--surface, #131C2E)',
        border: '1px solid var(--border, #2A3650)',
        borderRadius: 12,
        textAlign: 'center',
      }}>
        <div style={{ fontSize: 40, marginBottom: 10 }}>🏚️</div>
        <h1 style={{ margin: '0 0 6px', fontSize: 18, fontWeight: 700 }}>{title}</h1>
        <p style={{ margin: '0 0 18px', fontSize: 13, color: 'var(--text-muted, #94A3B8)' }}>
          {message}
        </p>
        <div style={{ display: 'flex', gap: 8 }}>
          <button onClick={onPrimary} disabled={busy} style={primaryButton}>
            {primaryLabel}
          </button>
          <button onClick={onSecondary} disabled={busy} style={ghostButton}>
            {secondaryLabel}
          </button>
        </div>
        <p style={{ margin: '14px 0 0', fontSize: 12, color: 'var(--text-muted, #64748B)' }}>
          {userLabel}
        </p>
      </div>
    </div>
  );
}

export function LoadingScreen({ message }: { message: string }): JSX.Element {
  return (
    <div style={{
      minHeight: '100vh',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: 24,
      background: 'var(--bg, #0B1220)',
      color: 'var(--text-muted, #94A3B8)',
      fontSize: 14,
    }}>
      {message}
    </div>
  );
}

/**
 * SOLID red with white text, so it reads on every theme. It used to be pale
 * pink text on a translucent red wash — fine on the dark login, red-on-red on
 * a light one (reported 2026-09-21 on the "cash drawer inactive" message).
 */
function ErrorBanner({ text }: { text: string }): JSX.Element {
  return (
    <div
      role="alert"
      style={{
        display: 'flex',
        alignItems: 'flex-start',
        gap: 10,
        marginBottom: 14,
        padding: '12px 14px',
        background: '#B91C1C',
        border: '1px solid #7F1D1D',
        borderRadius: 8,
        color: '#FFFFFF',
        fontSize: 15,
        fontWeight: 600,
        lineHeight: 1.45,
      }}
    >
      <span aria-hidden style={{ fontSize: 18, lineHeight: 1.2 }}>⚠</span>
      <span>{text}</span>
    </div>
  );
}

const ghostButton: React.CSSProperties = {
  flex: 1,
  minHeight: 48,
  padding: '10px 12px',
  background: 'transparent',
  border: '1px solid var(--border, #2A3650)',
  borderRadius: 8,
  color: 'var(--text-muted, #94A3B8)',
  fontSize: 14,
  cursor: 'pointer',
};

const primaryButton: React.CSSProperties = {
  flex: 1,
  minHeight: 48,
  padding: '10px 12px',
  background: 'var(--primary, #5478D4)',
  border: 'none',
  borderRadius: 8,
  color: '#fff',
  fontSize: 15,
  fontWeight: 600,
  cursor: 'pointer',
};
